/**
 * Phase 14.5 Step 4 — `/campaigns` real unified list page.
 * Phase 14.8 W2 — per-row Delete affordance (terminal-state campaigns only)
 *                 with AlertDialog confirm; calls the new
 *                 DELETE /api/{pk,geometry}-campaigns/{id} endpoints.
 *
 * Replaces the Step 1 placeholder. Fans out across both campaign
 * families and merges client-side per PHASE_14_5_PLAN §3 row 4.
 *
 *   • parallel `Promise.allSettled` over
 *       GET /api/pk-campaigns?page=1&page_size=50
 *       GET /api/geometry-campaigns?page=1&page_size=50
 *   • each row tagged with `family ∈ {"pk", "geometry"}` for the
 *     detail-dispatch fast path (Y5)
 *   • partial-failure UX (refinement D): when one family rejects and
 *     the other resolves, the resolved family's rows still render +
 *     a non-blocking banner names the failed family.
 *   • sorted by `created_at` desc (operator can flip to name asc).
 *
 * Phase 14.8 W2 — Delete affordance:
 *   • surfaces only for rows in a terminal status (complete / cancelled / failed)
 *   • AlertDialog confirms before issuing DELETE
 *   • optimistic removal from the list on 204; banner-shown error otherwise
 *
 * Empty-state copy preserves the Step 1 testid `campaigns-list-placeholder`
 * as the legitimate empty-state affordance.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDownUp, Compass, Plus, Loader2, AlertTriangle, Trash2, Copy } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/formatApiError";
import { scrubPhaseLabel } from "@/lib/displayName";
import pkCampaigns from "@/api/pkCampaigns";
import geometryCampaigns from "@/api/geometryCampaigns";
import optimisationCampaigns from "@/api/optimisationCampaigns";
import unifiedCampaigns from "@/api/unifiedCampaigns";
import { api as apiClient } from "@/api/client";

const PAGE_SIZE = 50;
const TERMINAL_STATUSES = ["complete", "cancelled", "failed"];

function _normalisePk(item) {
    return {
        id: item.id,
        name: item.name || item.id,
        family: "pk",
        campaign_type: item.campaign_type || "baseline",
        status: item.status,
        created_at: item.created_at,
        legacy: !!item.legacy,
    };
}

function _normaliseGeometry(item) {
    return {
        id: item.id,
        name: item.name || item.id,
        family: "geometry",
        campaign_type: item.campaign_type || "geometry_sweep",
        status: item.status,
        created_at: item.created_at,
        legacy: !!item.legacy,
    };
}

function _normaliseOptimisation(item) {
    return {
        id: item.id,
        name: item.name || item.id,
        family: "optimisation",
        campaign_type: "optimisation",
        status: item.status,
        created_at: item.created_at,
        legacy: !!item.legacy,
    };
}

function _normaliseUnified(item) {
    return {
        id: item.id,
        name: item.name || item.id,
        family: "unified",
        campaign_type: item.sampling_kind || "unified",
        status: item.status,
        created_at: item.created_at,
        legacy: !!item.legacy,
        // Phase 17.9a — demo fixture flag drives the "Demo" badge,
        // the filter chip, and hides the delete affordance (the
        // backend returns 410 GONE on delete anyway, but UX is
        // cleaner if the button is suppressed).
        is_demo_fixture: !!item.is_demo_fixture,
        // Phase 17.9d — surface the partial-finalisation flag for
        // the amber "Partial" sub-pill rendered next to "status:
        // cancelled".
        cancelled_with_partial_results: !!item.cancelled_with_partial_results,
    };
}

function _toEpoch(ts) {
    if (!ts) return 0;
    const t = Date.parse(ts);
    return Number.isFinite(t) ? t : 0;
}

export default function CampaignsListPage() {
    const [pkRows, setPkRows] = useState(null);
    const [geomRows, setGeomRows] = useState(null);
    const [optRows, setOptRows] = useState(null);
    const [unifiedRows, setUnifiedRows] = useState(null);
    const [pkErr, setPkErr] = useState(null);
    const [geomErr, setGeomErr] = useState(null);
    const [optErr, setOptErr] = useState(null);
    const [unifiedErr, setUnifiedErr] = useState(null);
    const [sortMode, setSortMode] = useState("created_at_desc");
    // Phase 17 — unified | legacy filter tab.
    const [filterMode, setFilterMode] = useState("unified");
    const [deleteErr, setDeleteErr] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    // Phase 18.A — Clone Campaign
    const [cloningId, setCloningId] = useState(null);
    const [cloneErr, setCloneErr] = useState(null);
    const navigate = useNavigate();
    // Phase 18.D — defensive-skip / invalid-campaigns banner (superadmin
    // only). The endpoint 403s for non-superadmins; we treat that as
    // "banner hidden" silently.
    const [invalidScan, setInvalidScan] = useState({ visible: false, count: 0 });
    const [purging, setPurging] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            pkCampaigns.list({ page: 1, page_size: PAGE_SIZE }),
            geometryCampaigns.list({ page: 1, page_size: PAGE_SIZE }),
            optimisationCampaigns.list({ limit: PAGE_SIZE }),
            unifiedCampaigns.list({ limit: PAGE_SIZE }),
        ]).then((results) => {
            if (cancelled) return;
            const [pkRes, geomRes, optRes, uniRes] = results;
            if (pkRes.status === "fulfilled") {
                const items = Array.isArray(pkRes.value?.items) ? pkRes.value.items : [];
                setPkRows(items.map(_normalisePk));
            } else {
                setPkRows([]);
                setPkErr(pkRes.reason?.message || "Pk campaigns unavailable.");
            }
            if (geomRes.status === "fulfilled") {
                const items = Array.isArray(geomRes.value?.items) ? geomRes.value.items : [];
                setGeomRows(items.map(_normaliseGeometry));
            } else {
                setGeomRows([]);
                setGeomErr(geomRes.reason?.message || "Geometry campaigns unavailable.");
            }
            if (optRes.status === "fulfilled") {
                const items = Array.isArray(optRes.value?.items) ? optRes.value.items : [];
                setOptRows(items.map(_normaliseOptimisation));
            } else {
                setOptRows([]);
                setOptErr(optRes.reason?.message || "Optimisation campaigns unavailable.");
            }
            if (uniRes.status === "fulfilled") {
                const items = Array.isArray(uniRes.value?.items) ? uniRes.value.items : [];
                setUnifiedRows(items.map(_normaliseUnified));
            } else {
                setUnifiedRows([]);
                setUnifiedErr(uniRes.reason?.message || "Unified campaigns unavailable.");
            }
        });
        return () => { cancelled = true; };
    }, []);

    // Phase 18.D — opportunistic invalid-scan. Tries the superadmin-only
    // endpoint; 403 = non-superadmin → banner stays hidden.
    useEffect(() => {
        let cancelled = false;
        apiClient
            .get("/api/admin/campaigns/invalid")
            .then((r) => {
                if (cancelled) return;
                const count = r?.data?.count || 0;
                setInvalidScan({ visible: count > 0, count });
            })
            .catch(() => {
                // Silent: 403 for non-superadmin is the common case.
                if (!cancelled) setInvalidScan({ visible: false, count: 0 });
            });
        return () => { cancelled = true; };
    }, []);

    const onPurgeInvalid = async () => {
        setPurging(true);
        try {
            await apiClient.post("/api/admin/campaigns/invalid/purge", {});
            setInvalidScan({ visible: false, count: 0 });
        } catch (e) {
            // Surface via existing delete-error banner — keeps UI tight.
            setDeleteErr(formatApiError(e, "Purge failed."));
        } finally {
            setPurging(false);
        }
    };

    // Phase 18.A — Clone Campaign. Only unified campaigns support clone;
    // the legacy families don't expose the endpoint.
    const onClone = async (row) => {
        setCloneErr(null);
        setCloningId(row.id);
        try {
            const cloned = await unifiedCampaigns.clone(row.id);
            navigate(`/campaigns/${cloned.id}`, {
                state: { family: "unified" },
            });
        } catch (e) {
            setCloneErr(formatApiError(e, "Clone failed."));
        } finally {
            setCloningId(null);
        }
    };

    const onDelete = async (row) => {
        setDeleteErr(null);
        setDeletingId(row.id);
        try {
            let api;
            if (row.family === "pk") api = pkCampaigns;
            else if (row.family === "geometry") api = geometryCampaigns;
            else if (row.family === "unified") api = unifiedCampaigns;
            else api = optimisationCampaigns;
            await api.remove(row.id);
            if (row.family === "pk") {
                setPkRows((prev) => (prev || []).filter((r) => r.id !== row.id));
            } else if (row.family === "geometry") {
                setGeomRows((prev) => (prev || []).filter((r) => r.id !== row.id));
            } else if (row.family === "unified") {
                setUnifiedRows((prev) => (prev || []).filter((r) => r.id !== row.id));
            } else {
                setOptRows((prev) => (prev || []).filter((r) => r.id !== row.id));
            }
        } catch (e) {
            setDeleteErr(formatApiError(e, `Failed to delete campaign '${scrubPhaseLabel(row.name)}'`));
        } finally {
            setDeletingId(null);
        }
    };

    const loading = pkRows === null || geomRows === null || optRows === null || unifiedRows === null;
    const demoCount = useMemo(
        () => (unifiedRows || []).filter((r) => r.is_demo_fixture).length,
        [unifiedRows],
    );
    const merged = useMemo(() => {
        const all = [
            ...(pkRows || []),
            ...(geomRows || []),
            ...(optRows || []),
            ...(unifiedRows || []),
        ];
        let filtered;
        if (filterMode === "legacy") {
            filtered = all.filter((r) => r.legacy);
        } else if (filterMode === "demos") {
            filtered = all.filter((r) => r.is_demo_fixture);
        } else {
            // "unified" (Active) — operator's own work, demos hidden
            // by default so they don't compete for attention.
            filtered = all.filter((r) => !r.legacy && !r.is_demo_fixture);
        }
        if (sortMode === "name_asc") {
            return [...filtered].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        }
        return [...filtered].sort((a, b) => _toEpoch(b.created_at) - _toEpoch(a.created_at));
    }, [pkRows, geomRows, optRows, unifiedRows, sortMode, filterMode]);

    return (
        <div className="space-y-4" data-testid="campaigns-list-page">
            <PageHeader
                title="Campaigns"
                subtitle="Single workflow for Pk effectiveness, sensitivity, target-Pk, geometry-sweep, and degradation campaigns. Toggle the dimensions you need; the wizard dispatches to the right backend."
                actions={[
                    <Button
                        key="new"
                        asChild
                        data-testid="campaigns-new-button"
                        className="gap-2"
                    >
                        <Link to="/campaigns/new">
                            <Plus className="size-4" />
                            New campaign
                        </Link>
                    </Button>,
                ]}
            />

            {pkErr && !geomErr && (
                <Alert
                    data-testid="campaigns-list-partial-error-pk"
                    className="border-amber-500/40 bg-amber-500/10"
                >
                    <AlertTriangle className="size-4" />
                    <AlertDescription className="text-amber-200">
                        Pk campaigns unavailable — showing Geometry only. ({pkErr})
                    </AlertDescription>
                </Alert>
            )}
            {geomErr && !pkErr && (
                <Alert
                    data-testid="campaigns-list-partial-error-geometry"
                    className="border-amber-500/40 bg-amber-500/10"
                >
                    <AlertTriangle className="size-4" />
                    <AlertDescription className="text-amber-200">
                        Geometry campaigns unavailable — showing Pk only. ({geomErr})
                    </AlertDescription>
                </Alert>
            )}
            {pkErr && geomErr && (
                <Alert variant="destructive" data-testid="campaigns-list-full-error">
                    <AlertTriangle className="size-4" />
                    <AlertDescription>Both campaign families unavailable.</AlertDescription>
                </Alert>
            )}

            {deleteErr && (
                <Alert
                    variant="destructive"
                    data-testid="campaigns-list-delete-error"
                >
                    <AlertTriangle className="size-4" />
                    <AlertDescription>{deleteErr}</AlertDescription>
                </Alert>
            )}

            {cloneErr && (
                <Alert
                    variant="destructive"
                    data-testid="campaigns-list-clone-error"
                >
                    <AlertTriangle className="size-4" />
                    <AlertDescription>{cloneErr}</AlertDescription>
                </Alert>
            )}

            {/* Phase 18.D — defensive-skip / invalid-campaigns banner.
                Superadmin only. Shows the count of docs the list endpoint
                silently skipped and offers a one-click
                purge. */}
            {invalidScan.visible && (
                <Alert
                    data-testid="campaigns-list-invalid-banner"
                    className="border-amber-500/40 bg-amber-500/10"
                >
                    <AlertTriangle className="size-4 text-amber-300" />
                    <AlertDescription className="flex flex-wrap items-center gap-3 text-amber-200">
                        <span>
                            <strong>{invalidScan.count}</strong> invalid campaign
                            {invalidScan.count === 1 ? "" : "s"} skipped from the list
                            (missing required fields). Visible to superadmins only.
                        </span>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 border-amber-500/50 text-[11px] text-amber-100 hover:bg-amber-500/20"
                            disabled={purging}
                            onClick={onPurgeInvalid}
                            data-testid="campaigns-list-invalid-purge"
                        >
                            {purging ? (
                                <Loader2 className="size-3 animate-spin" />
                            ) : (
                                "Purge now"
                            )}
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex items-center gap-2 text-xs text-ux-text-3">
                {loading && (
                    <>
                        <Loader2 className="size-4 animate-spin" data-testid="campaigns-list-loading" />
                        <span>Loading…</span>
                    </>
                )}
                {!loading && (
                    <>
                        <span data-testid="campaigns-list-totals">
                            {merged.length} shown · {(unifiedRows?.length ?? 0)} unified ·{" "}
                            {(pkRows?.length ?? 0) + (geomRows?.length ?? 0) + (optRows?.length ?? 0)} legacy
                            {demoCount > 0 && (
                                <>
                                    {" "}· <span data-testid="campaigns-list-demo-count">
                                        {demoCount} demo{demoCount === 1 ? "" : "s"}
                                    </span>
                                </>
                            )}
                        </span>
                        <div className="ml-auto flex items-center gap-1 rounded-md border border-ux-border-1 bg-ux-surface-1 p-0.5"
                             data-testid="campaigns-list-filter-tabs">
                            {[
                                { key: "unified", label: "Active" },
                                { key: "legacy", label: "Legacy" },
                                { key: "demos", label: `Demos${demoCount ? ` (${demoCount})` : ""}` },
                            ].map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setFilterMode(t.key)}
                                    className={[
                                        "rounded px-2 py-1 text-xs",
                                        filterMode === t.key
                                            ? "bg-ux-surface-2 text-ux-text-1"
                                            : "text-ux-text-3 hover:text-ux-text-1",
                                    ].join(" ")}
                                    data-testid={`campaigns-list-filter-${t.key}`}
                                    data-active={filterMode === t.key ? "1" : "0"}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1"
                            onClick={() => setSortMode((m) => m === "name_asc" ? "created_at_desc" : "name_asc")}
                            data-testid="campaigns-list-sort-toggle"
                            data-sort-mode={sortMode}
                        >
                            <ArrowDownUp className="size-3" />
                            Sort: {sortMode === "name_asc" ? "Name (A→Z)" : "Created (newest)"}
                        </Button>
                    </>
                )}
            </div>

            {!loading && merged.length === 0 && !pkErr && !geomErr && (
                <div
                    className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-8 text-center text-sm text-ux-text-3"
                    data-testid="campaigns-list-placeholder"
                >
                    <Compass className="mx-auto mb-3 size-6 opacity-60" />
                    <p className="font-medium text-ux-text-2">No campaigns yet.</p>
                    <p className="mt-1">
                        Use{" "}
                        <Link to="/campaigns/new" className="underline" data-testid="campaigns-list-new-link">
                            the new-campaign builder
                        </Link>{" "}
                        to create the first one.
                    </p>
                </div>
            )}

            {merged.length > 0 && (
                <ul
                    className="divide-y divide-ux-border-1 rounded-md border border-ux-border-1 bg-ux-surface-1"
                    data-testid="campaigns-list"
                >
                    {merged.map((row) => (
                        <li
                            key={`${row.family}-${row.id}`}
                            className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-4 py-3"
                            data-testid={`campaigns-row-${row.id}`}
                            data-family={row.family}
                        >
                            <div>
                                <div className="flex items-baseline gap-2">
                                    <Link
                                        to={`/campaigns/${row.id}`}
                                        state={{ family: row.family }}
                                        className="text-sm font-medium text-ux-text-1 underline-offset-2 hover:underline"
                                        data-testid={`campaigns-row-link-${row.id}`}
                                    >
                                        {scrubPhaseLabel(row.name)}
                                    </Link>
                                    <span
                                        className={[
                                            "rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide",
                                            row.family === "pk"
                                                ? "border-blue-500/40 bg-blue-500/10 text-blue-300"
                                                : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
                                        ].join(" ")}
                                        data-testid={`campaigns-row-family-${row.id}`}
                                    >
                                        {row.family}
                                    </span>
                                    {row.is_demo_fixture && (
                                        <span
                                            className="rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300"
                                            data-testid={`campaigns-row-demo-badge-${row.id}`}
                                            title="Demo fixture — read-only. Shipped with the deploy so the client-ready demo set is present out of the box."
                                        >
                                            Demo
                                        </span>
                                    )}
                                    <span className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                        {row.campaign_type}
                                    </span>
                                </div>
                                {row.status && (
                                    <p className="text-xs text-ux-text-3">
                                        status: {row.status}
                                        {row.cancelled_with_partial_results && (
                                            <span
                                                className="ml-1 inline-flex items-center rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-300"
                                                data-testid={`campaigns-row-partial-pill-${row.id}`}
                                                title="Cancelled with partial results preserved"
                                            >
                                                Partial
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                            <Link
                                to={`/campaigns/${row.id}`}
                                state={{ family: row.family }}
                                className="text-xs underline"
                                data-testid={`campaigns-row-open-${row.id}`}
                            >
                                Open →
                            </Link>
                            {row.family === "unified" && !row.is_demo_fixture ? (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-ux-text-3 hover:text-ux-text-1"
                                    disabled={cloningId === row.id}
                                    onClick={() => onClone(row)}
                                    data-testid={`campaigns-row-clone-${row.id}`}
                                    aria-label={`Clone campaign ${scrubPhaseLabel(row.name)}`}
                                    title="Clone this campaign and run a fresh copy"
                                >
                                    {cloningId === row.id ? (
                                        <Loader2 className="size-4 animate-spin" />
                                    ) : (
                                        <Copy className="size-4" />
                                    )}
                                </Button>
                            ) : (
                                <span aria-hidden />
                            )}
                            {TERMINAL_STATUSES.includes(row.status) && !row.is_demo_fixture && (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-ux-text-3 hover:text-red-400"
                                            disabled={deletingId === row.id}
                                            data-testid={`campaigns-row-delete-${row.id}`}
                                            aria-label={`Delete campaign ${scrubPhaseLabel(row.name)}`}
                                        >
                                            {deletingId === row.id ? (
                                                <Loader2 className="size-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="size-4" />
                                            )}
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent
                                        data-testid={`campaigns-row-delete-confirm-${row.id}`}
                                    >
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>
                                                Delete this {row.family} campaign?
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                <span className="font-medium text-ux-text-1">{scrubPhaseLabel(row.name)}</span> —
                                                this removes the campaign record AND any on-disk trajectory
                                                artefacts. This action is irreversible.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel
                                                data-testid={`campaigns-row-delete-confirm-no-${row.id}`}
                                            >
                                                Keep
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => onDelete(row)}
                                                data-testid={`campaigns-row-delete-confirm-yes-${row.id}`}
                                            >
                                                Delete campaign
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
